// src/App.tsx - COMPLETE FIXED with all game states
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { auth, db } from './services/firebase';
import { signOut } from 'firebase/auth';
import { 
  collection, addDoc, serverTimestamp, doc, updateDoc, getDoc,
  arrayUnion, arrayRemove, query, where, onSnapshot, writeBatch, increment
} from 'firebase/firestore';
import { Loader2, Lock, User, LogOut } from 'lucide-react';

// 🟢 SEO & ANALYTICS IMPORTS
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';

// --- LAYOUTS ---
import { ClientLayout } from './components/layouts/ClientLayout';

// --- CUSTOM HOOKS ---
import { useAuth } from './hooks/useAuth';
import { useProducts } from './hooks/useProducts';
import { useProfile } from './hooks/useProfile';
import { useUnreadMessages } from './hooks/useUnreadMessages';

// 🟢 NEW CART CONTEXT
import { useCart, CartProvider } from './contexts/CartContext';

// --- SHARED COMPONENTS ---
import { Toast } from './components/shared/Toast';
import { NotificationsModal } from './components/shared/NotificationsModal';
import { InAppNotification, NotificationData } from './components/shared/InAppNotification';
import { ShareModal } from './components/community/ShareModal';
import { Portal } from './components/shared/Portal';

// --- VIEW COMPONENTS ---
import { HomeView } from './components/views/HomeView';
import { SearchView } from './components/views/SearchView';
import { CommunityView } from './components/views/CommunityView';
import { ProfileView } from './components/views/ProfileView';
import { PublicProfileView } from './components/views/PublicProfileView';
import { CollectionsView } from './components/views/CollectionsView'; 
import { ReferralLanding } from './components/views/ReferralLanding';
import { HypeCommerce } from './components/views/hypecommerce';
import { ThriftView } from './components/views/ThriftView';
import { DesignerGateView } from './components/views/DesignerGateView';
import { JewelleryView } from './components/views/JewelleryView';

// 🟢 HYPE ARCADE VIEW IMPORT
import { HypeArcadeView } from './components/views/HypeArcadeView';

// 🟢 GAME MODALS IMPORTS
import { Hyce } from './components/games/Hyce';
import { HypeStacker } from './components/games/HypeStacker';
import { HypeHighRoller } from './components/games/BallDrop';
import { GlassBridge } from './components/games/GlassBridge';
import { Zwepz } from './components/games/Zwepz';
import { MysteryDropModal } from './components/games/MysteryDropModal';

// 🟢 THRIFT PROFILE IMPORT
import { ThriftProfilePage } from './components/shopping/ThriftProfileModal';
// 🟢 DESIGNER PROFILE IMPORT
import { DesignerProfilePage } from './components/shopping/DesignerProfileModal';

// --- AUTH & ONBOARDING ---
import { AuthModal } from './components/auth/AuthScreen';
import { ProfileSetup } from './components/auth/ProfileSetup';
import { StyleOnboarding } from './components/auth/StyleOnboarding';

// --- SHOPPING MODALS ---
import { ProductDetailModal } from './components/shopping/ProductDetailModal';
import { CartOverlay } from './components/shopping/CartOverlay';
import { PaymentModal } from './components/shopping/PaymentModal';
import { OrderTrackingModal } from './components/shopping/OrderTrackingModal';
import { BrandProfileView } from './components/shopping/BrandProfileModal';
import { LoveScoopStore } from './components/platform/LoveScoopStore'; 

// --- TOOL MODALS ---
import { ScoopAIModal } from './components/tools/ScoopAIModal';
import { VirtualTryOnModal } from './components/tools/VirtualTryOn';
import { SizeGuideModal } from './components/tools/SizeGuideModal';

// --- SETTINGS MODALS ---
import { BuyCreditsModal } from './components/settings/BuyCreditsModal';
import { InviteFriendsModal } from './components/settings/InviteFriendsModal';
import { EditProfileModal } from './components/settings/EditProfileModal';
import { SettingsModal } from './components/settings/SettingsModal'; 

// --- SOCIAL MODALS ---
import { CreationMenu } from './components/community/CreationMenu';
import { MessagesPanel } from './components/social/MessagesPanel';
import { CreatePostModal } from './components/community/CreatePostModal';
import { MessageNotifier } from './components/social/MessageNotifier';

// --- GAME MODALS ---
import { CopOrDropModal } from './components/games/CopOrDropModal';
import { HypeCrashModal } from './components/games/HypeCrashModal';
import { HypeMinesModal } from './components/games/HypeMinesModal';
import { HypeShellsModal } from './components/games/HypeShellsModal';
import { ThreadForThread } from './components/games/ThreadForThread';
// --- UTILS & TYPES ---
import { generateOrderNumber } from './utils/helpers';
import { Product, Order, UserProfile } from './types';

// ===========================================================================
// 0.5 INITIALIZE ANALYTICS 
// ===========================================================================
ReactGA.initialize("G-XXXXXXXXXX");

// 🟢 DYNAMIC PROFILE WRAPPER COMPONENT
const DynamicPublicProfile = ({ currentUser, onFollow, onUnfollow, onMessage, onSelectProduct }: any) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        
        if (currentUser?.uid === id) {
            navigate('/profile', { replace: true });
            return;
        }

        const fetchProfile = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, 'users', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setProfileData({ uid: docSnap.id, ...docSnap.data() } as UserProfile);
                } else {
                    setProfileData(null);
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
            setLoading(false);
        };
        fetchProfile();
    }, [id, currentUser?.uid, navigate]);

    if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-white" size={32} /></div>;
    if (!profileData) return <div className="min-h-screen bg-black flex items-center justify-center text-white font-black uppercase tracking-widest text-xl">User not found</div>;

    return (
        <PublicProfileView 
            viewingProfile={profileData} 
            currentUser={currentUser || undefined} 
            onBack={() => navigate(-1)} 
            onFollow={onFollow} 
            onUnfollow={onUnfollow} 
            onMessage={onMessage}
            onSelectProduct={onSelectProduct}
        />
    );
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // 🟢 PAGE VIEW TRACKER FOR GOOGLE ANALYTICS
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);

  // 🟢 CATCH REFERRAL CODES
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
        localStorage.setItem('scoop_referrer', refCode);
    }
  }, [location.search]);
  
  // ===========================================================================
  // GLOBAL BRANDING ENFORCEMENT
  // ===========================================================================
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'shortcut icon';
    link.href = '/favicon.ico.svg'; 

    if (!document.querySelector("link[rel*='icon']")) {
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    if (!location.pathname.includes('/profile/')) {
       document.title = "Scoophype";
    }
  }, [location]); 

  // ===========================================================================
  // 1. APP STATE
  // ===========================================================================
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [directCheckoutItem, setDirectCheckoutItem] = useState<any[] | null>(null);

  // Data Context
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [trackingOrder, setTrackingOrder] = useState<Order | null>(null);
  const [aiContextProduct, setAiContextProduct] = useState<Product | null>(null);
  
  const [shareData, setShareData] = useState<{ type: 'post', payload: any } | null>(null);

  // 🟢 GATEKEEPER & AUTH STATE
  const [isAuthPromptOpen, setIsAuthPromptOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMessage, setAuthMessage] = useState("Join Scoop Hype to interact with the culture.");

  // Modal Visibility
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  
  const [isScoopAIOpen, setIsScoopAIOpen] = useState(false);
  const [isVirtualTryOnOpen, setIsVirtualTryOnOpen] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  
  const [isCreationMenuOpen, setIsCreationMenuOpen] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const [isInviteFriendsOpen, setIsInviteFriendsOpen] = useState(false);
  
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const [isCopOrDropOpen, setIsCopOrDropOpen] = useState(false);
  const [isCreateHookOpen, setIsCreateHookOpen] = useState(false);

  // 🟢 GAME STATES - ADD THESE
  const [isMysteryDropOpen, setIsMysteryDropOpen] = useState(false);
  const [isHyceOpen, setIsHyceOpen] = useState(false);
  const [isHypeStackerOpen, setIsHypeStackerOpen] = useState(false);
  const [isBallDropOpen, setIsBallDropOpen] = useState(false);
  const [isGlassBridgeOpen, setIsGlassBridgeOpen] = useState(false);
  const [isZwepzOpen, setIsZwepzOpen] = useState(false);

  // Game States
  const [isHypeCrashOpen, setIsHypeCrashOpen] = useState(false);
  const [isHypeMinesOpen, setIsHypeMinesOpen] = useState(false);
  const [isHypeShellsOpen, setIsHypeShellsOpen] = useState(false);
  const [isThreadGameOpen, setIsThreadGameOpen] = useState(false);

  // Component Specific State
  const [activeMessageUserId, setActiveMessageUserId] = useState<string | null>(null);
  const [activeMessageText, setActiveMessageText] = useState<string>(''); 
  const [activeNotification, setActiveNotification] = useState<NotificationData | null>(null);
  
  const [pendingGiftAction, setPendingGiftAction] = useState<{ 
      type: 'send' | 'request' | 'share_product' | 'share_brand', 
      data: any 
  } | null>(null);

  const [gameScore, setGameScore] = useState(0); 
  const [currentGameItem, setCurrentGameItem] = useState(0);

  const [toast, setToast] = useState({ 
    show: false, 
    message: '', 
    type: 'success' as 'success' | 'error' | 'warning' 
  });

  // ===========================================================================
  // 2. DATA HOOKS
  // ===========================================================================
  const { user, profile, loadingProfile, updateProfile } = useAuth();
  const { products, brands } = useProducts(); 
  
  const { 
    cartItems: cart, addToCart, updateQuantity: updateCartQuantity, 
    removeFromCart, clearCart 
  } = useCart();
  
  const { 
    orders, notifications, wishlist, setWishlist 
  } = useProfile(user?.uid);

  // ===========================================================================
  // 🟢🟢🟢 FILTER BRANDS FOR HOMEVIEW - EXCLUDE THRIFT AND DESIGNER 🟢🟢🟢
  // ===========================================================================
  const brandProfiles = useMemo(() => {
    console.log('🟢🟢🟢 STARTING BRAND FILTER 🟢🟢🟢');
    
    const filtered = brands.filter((b: any) => {
      const workspaceType = (b.workspaceType || '').toLowerCase();
      const userType = (b.userType || '').toLowerCase();
      const type = (b.type || '').toLowerCase();
      const role = (b.role || '').toLowerCase();
      const sellerType = (b.sellerType || '').toLowerCase();
      const businessType = (b.businessType || '').toLowerCase();
      const accountType = (b.accountType || '').toLowerCase();
      const category = (b.category || '').toLowerCase();
      
      const name = (b.businessName || b.displayName || b.name || '').toLowerCase();
      
      // EXCLUDE thrift
      const isThrift = workspaceType === 'thrift' || 
                       userType === 'thrift' || 
                       type === 'thrift' || 
                       role === 'thrift' ||
                       sellerType === 'thrift' ||
                       businessType === 'thrift' ||
                       accountType === 'thrift' ||
                       category === 'thrift' ||
                       name.includes('thrift') ||
                       name.includes('vintage') ||
                       name.includes('second hand');
      
      // EXCLUDE designer
      const isDesigner = workspaceType === 'designer' || 
                         userType === 'designer' || 
                         type === 'designer' || 
                         role === 'designer' ||
                         sellerType === 'designer' ||
                         businessType === 'designer' ||
                         accountType === 'designer' ||
                         category === 'designer' ||
                         name.includes('designer') ||
                         name.includes('couture') ||
                         name.includes('atelier');
      
      return !isThrift && !isDesigner;
    });
    
    return filtered;
  }, [brands]);

  // ===========================================================================
  // 3. THE GATEKEEPER INTERCEPTOR
  // ===========================================================================
  const requireAuth = (actionCallback: () => void, customMessage?: string) => {
    if (!user || !profile) {
      setAuthMessage(customMessage || "You need to create an account or sign in to interact with the culture.");
      setIsAuthPromptOpen(true); 
    } else {
      actionCallback(); 
    }
  };

  // ===========================================================================
  // 4. EVENT LISTENERS & URL SHARING
  // ===========================================================================
  useEffect(() => {
    const handleOpenMenu = () => requireAuth(() => setIsCreationMenuOpen(true), "Log in to create a post.");
    const handleCopOrDrop = () => requireAuth(() => {
        setGameScore(0);
        setCurrentGameItem(0);
        setIsCopOrDropOpen(true);
    }, "Log in to play Cop or Drop.");
    const handleMessages = () => requireAuth(() => setIsMessagesOpen(true), "Log in to view your messages.");
    const handleNotifications = () => requireAuth(() => setIsNotificationsOpen(true), "Log in to view notifications.");
    const handleScoopAI = () => requireAuth(() => {
        setAiContextProduct(null);
        setIsScoopAIOpen(true);
    }, "Log in to use Scoop AI.");
    const handleOpenCart = () => setIsCartOpen(true); 

    const handleHypeCrash = () => requireAuth(() => setIsHypeCrashOpen(true), "Log in to play Hype Crash.");
    const handleHypeMines = () => requireAuth(() => setIsHypeMinesOpen(true), "Log in to play Hype Mines.");
    const handleHypeShells = () => requireAuth(() => setIsHypeShellsOpen(true), "Log in to play Hype Dash.");
    const handleThreadGame = () => requireAuth(() => setIsThreadGameOpen(true), "Log in to play Thread for Thread.");
    const handleTopNavLogin = () => {
        setAuthMessage("Log in to join the society and shop exclusive drops.");
        setIsAuthModalOpen(true);
    };

    window.addEventListener('open-creation-menu', handleOpenMenu);
    window.addEventListener('open-cop-or-drop', handleCopOrDrop);
    window.addEventListener('open-messages', handleMessages);
    window.addEventListener('open-notifications', handleNotifications);
    window.addEventListener('open-scoop-ai', handleScoopAI);
    window.addEventListener('open-cart', handleOpenCart);
    window.addEventListener('open-login-modal', handleTopNavLogin); 
    
    window.addEventListener('open-hype-crash', handleHypeCrash);
    window.addEventListener('open-hype-mines', handleHypeMines);
    window.addEventListener('open-hype-shells', handleHypeShells);
    window.addEventListener('open-thread-game', handleThreadGame);

    return () => {
      window.removeEventListener('open-creation-menu', handleOpenMenu);
      window.removeEventListener('open-cop-or-drop', handleCopOrDrop);
      window.removeEventListener('open-messages', handleMessages);
      window.removeEventListener('open-notifications', handleNotifications);
      window.removeEventListener('open-scoop-ai', handleScoopAI);
      window.removeEventListener('open-cart', handleOpenCart);
      window.removeEventListener('open-login-modal', handleTopNavLogin);
      window.removeEventListener('open-hype-crash', handleHypeCrash);
      window.removeEventListener('open-hype-mines', handleHypeMines);
      window.removeEventListener('open-hype-shells', handleHypeShells);
      window.removeEventListener('open-thread-game', handleThreadGame);
    };
  }, [user, profile]);

  // ===========================================================================
  // URL SHARING
  // ===========================================================================
  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const sharedBrandId = params.get('brand');
  const sharedProductId = params.get('product');
  const sharedPostId = params.get('post');

  if (sharedPostId && location.pathname !== '/community' && location.pathname !== '/thrifts') {
    navigate(`/community?post=${sharedPostId}`, { replace: true });
    return;
  }

    if (sharedBrandId && brands.length > 0) {
      const foundBrand = brands.find(b => b.id === sharedBrandId || (b as any).businessName === sharedBrandId);
      if (foundBrand) setActiveBrand(foundBrand.id);
    }

    if (sharedProductId && products.length > 0) {
      const foundProduct = products.find(p => p.id === sharedProductId);
      if (foundProduct) setSelectedProduct(foundProduct);
    }
  }, [location.search, products, brands, navigate, location.pathname]);

  // ===========================================================================
  // 5. ACTION HANDLERS
  // ===========================================================================

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // ===========================================================================
  // 🟢🟢🟢 FIX: handleViewBrand - Detect profile type and open correct modal 🟢🟢🟢
  // ===========================================================================
  const handleViewBrand = (identifier: string) => {
    console.log('🔍 handleViewBrand called with:', identifier);
    
    const selectedBrand = brands.find(b => 
        b.id === identifier || 
        (b as any).businessName === identifier || 
        b.name === identifier
    );
    
    if (!selectedBrand) {
      console.warn('Brand not found:', identifier);
      setActiveBrand(identifier);
      return;
    }
    
    const sb = selectedBrand as any;
    const workspaceType = sb.workspaceType || '';
    const userType = sb.userType || '';
    const type = sb.type || '';
    const role = sb.role || '';
    
    const businessName = (sb.businessName || '').toLowerCase();
    const displayName = (sb.displayName || '').toLowerCase();
    
    const isThrift = workspaceType === 'thrift' || 
                     userType === 'thrift' || 
                     type === 'thrift' || 
                     role === 'thrift' ||
                     businessName.includes('thrift') ||
                     displayName.includes('thrift');
    
    const isDesigner = workspaceType === 'designer' || 
                       userType === 'designer' || 
                       type === 'designer' || 
                       role === 'designer' ||
                       businessName.includes('designer') ||
                       displayName.includes('designer');
    
    if (isThrift) {
      console.log('🟢 Opening Thrift Profile for:', sb.businessName);
      navigate(`/thrift-profile/${sb.id}`);
      return;
    }
    
    if (isDesigner) {
      console.log('🟢 Opening Designer Profile for:', sb.businessName);
      navigate(`/designer-profile/${sb.id}`);
      return;
    }
    
    console.log('🟢 Opening Brand Profile for:', sb.businessName);
    setActiveBrand(sb.id);
  };

  const handleFollowUser = async (targetUid: string) => {
    requireAuth(async () => {
        if (!profile || !user) return;
        
        updateProfile({
            followingUsers: [...(profile.followingUsers || []), targetUid]
        });

        try {
            const batch = writeBatch(db);
            const myRef = doc(db, 'users', profile.uid);
            batch.update(myRef, { followingUsers: arrayUnion(targetUid) });

            const theirRef = doc(db, 'users', targetUid);
            batch.update(theirRef, {
                followers: arrayUnion(profile.uid),
                followersCount: increment(1)
            });

            const notifRef = doc(collection(db, "notifications"));
            batch.set(notifRef, {
                userId: targetUid,
                type: 'follow',
                title: 'New Follower 👤',
                message: `${profile.displayName || 'Someone'} followed you.`,
                read: false,
                createdAt: serverTimestamp(),
                data: { followerId: profile.uid }
            });

            await batch.commit();
            showToast("Followed!");
        } catch (e) { 
            console.error("Follow Error:", e); 
            showToast("Failed to follow user", "error");
        }
    }, "Log in to follow this user.");
  };

  const handleUnfollowUser = async (targetUid: string) => {
    requireAuth(async () => {
        if (!profile) return;

        updateProfile({
            followingUsers: (profile.followingUsers || []).filter(id => id !== targetUid)
        });

        try {
            const batch = writeBatch(db);
            const myRef = doc(db, 'users', profile.uid);
            batch.update(myRef, { followingUsers: arrayRemove(targetUid) });

            const theirRef = doc(db, 'users', targetUid);
            batch.update(theirRef, {
                followers: arrayRemove(profile.uid),
                followersCount: increment(-1)
            });

            await batch.commit();
            showToast("Unfollowed.");
        } catch (e) { 
            console.error(e); 
            showToast("Error unfollowing user", "error");
        }
    });
  };

  const handleToggleWishlist = async (productId: string) => {
    requireAuth(async () => {
        const currentIds = (wishlist || [])
          .filter(item => item !== null && item !== undefined)
          .map(item => typeof item === 'object' ? (item as any).productId : item);

        const exists = currentIds.includes(productId);
        let newWishlist = exists ? currentIds.filter(id => id !== productId) : [...currentIds, productId]; 

        if (setWishlist) setWishlist(newWishlist); 

        if (profile?.uid) {
          try {
            const userRef = doc(db, 'users', profile.uid);
            await updateDoc(userRef, { wishlist: newWishlist });
          } catch (error) {
            console.error("Error saving wishlist:", error);
          }
        }
    }, "Log in to save this item to your wishlist.");
  };

  const handleCheckoutComplete = async (shippingDetails: any) => {
    if (isProcessingOrder) return;
    setIsProcessingOrder(true);

    const checkoutCart = directCheckoutItem ? directCheckoutItem : cart;
    if (!user || !profile || checkoutCart.length === 0) {
        setIsProcessingOrder(false); 
        return;
    }

    const orderNumber = generateOrderNumber();
    const subtotal = checkoutCart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const shipping = shippingDetails.shippingCost || (subtotal > 1000 ? 0 : 99); 
    const tax = subtotal * 0.15;
    const total = subtotal + shipping + tax;

    try {
      const orderRef = await addDoc(collection(db, "orders"), {
        userId: user.uid,
        businessId: shippingDetails.businessId || checkoutCart[0].businessId || 'platform',
        orderNumber,
        status: 'pending',
        items: checkoutCart, 
        total,
        subtotal,
        shipping,
        tax,
        deliveryMethod: shippingDetails.deliveryMethod || 'Standard Courier',
        courier: shippingDetails.courierId || 'standard', 
        shippingAddress: {
          id: 'temp',
          name: shippingDetails.name,
          address: shippingDetails.street,
          city: shippingDetails.city,
          province: 'Gauteng',
          postalCode: shippingDetails.code,
          country: 'South Africa',
          phone: shippingDetails.phone,
          isDefault: true
        },
        paymentMethod: {
          id: 'temp',
          type: shippingDetails.method, 
          brand: shippingDetails.method === 'card' ? 'VISA' : shippingDetails.method.toUpperCase(),
          last4: shippingDetails.card ? shippingDetails.card.slice(-4) : 'N/A',
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (!directCheckoutItem) {
          clearCart();
      }
      
      setIsPaymentOpen(false);
      showToast(`Order #${orderNumber} placed via ${shippingDetails.deliveryMethod}!`);
      
      navigate('/profile');
      
      await addDoc(collection(db, "notifications"), {
        userId: user.uid,
        type: 'order',
        title: 'Order Received 📦',
        message: `Your order #${orderNumber} will be shipped via ${shippingDetails.deliveryMethod}.`,
        createdAt: serverTimestamp(),
        read: false,
        relatedId: orderRef.id
      });
      
    } catch (error) {
      console.error("Error creating order:", error);
      showToast('Failed to place order. Please try again.', 'error');
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const handlePurchaseCredits = async (packageId: string, newCredits: number) => {
    if (!profile) return;
    try {
      await updateProfile({ cred: newCredits });
      const currentCred = profile.cred || 0;
      showToast(`${newCredits - currentCred} credits added to your account!`);
      setIsBuyCreditsOpen(false);
    } catch (error) {
      console.error("Error purchasing credits:", error);
      showToast('Failed to purchase credits', 'error');
    }
  };

  const handleCreationAction = (action: string) => {
    setIsCreationMenuOpen(false);
    requireAuth(() => {
        switch (action) {
          case 'post':
            setIsPostModalOpen(true);
            break;
          case 'canvas':
            setIsVirtualTryOnOpen(true);
            break;
          case 'ai':
            setAiContextProduct(null);
            setIsScoopAIOpen(true);
            break;
        }
    }, "Log in to access creation tools.");
  };

  const handleBuyNow = (product: Product, size: string, color: string, quantity: number) => {
    requireAuth(() => {
        const p = product as any; 
        const singleItem = [{
            ...product,
            selectedSize: size,
            selectedColor: color,
            quantity,
            price: p.onSale || (p.salePrice && p.salePrice > 0) ? p.salePrice : (p.regularPrice || p.price)
        }];

        setDirectCheckoutItem(singleItem);
        setIsPaymentOpen(true);
    }, "Log in to checkout.");
  };

  const handleOpenAI = (product: Product) => {
    requireAuth(() => {
        setAiContextProduct(product);
        setIsScoopAIOpen(true);
    }, "Log in to use Scoop AI.");
  };

  const handleTrackOrder = (order: Order) => {
    setTrackingOrder(order);
    setIsTrackingOpen(true);
  };

  const handleOpenCopOrDrop = () => {
    requireAuth(() => {
        setIsCopOrDropOpen(true);
        setGameScore(0);
        setCurrentGameItem(0);
    }, "Log in to play Cop or Drop.");
  };

  const handleCopVote = () => {
    const product = products[currentGameItem];
    if (product) {
      addToCart({ ...product, selectedSize: 'M', selectedColor: 'Default', quantity: 1 });
      showToast(`Copped! Added ${product.name} to cart`, 'success');
      setGameScore(prev => prev + 1); 
    }
    setCurrentGameItem(prev => prev + 1);
  };

  const handleDropVote = () => {
    setCurrentGameItem(prev => prev + 1);
  };

  const handleGameComplete = () => {
    setIsCopOrDropOpen(false);
    if (gameScore > 0) {
        showToast(`Shopping spree over! You added ${gameScore} items to your cart.`, 'success');
        setIsCartOpen(true); 
    } else {
        showToast('No items copped this time.', 'warning');
    }
    setGameScore(0);
    setCurrentGameItem(0);
  };

  // ===========================================================================
  // 6. DB LISTENERS 
  // ===========================================================================
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "messages"), where("recipientId", "==", user.uid), where("read", "==", false));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const msg = change.doc.data();
          if (!isMessagesOpen) {
            setActiveNotification({
              id: change.doc.id,
              title: msg.senderName || 'New Message',
              message: msg.text || 'Sent a message',
              image: msg.senderAvatar,
              type: 'message'
            });
          }
        }
      });
    }, (error) => console.error("Notification Listener Error:", error.message));
    return () => unsubscribe();
  }, [user, isMessagesOpen]);

  const prevCountRef = useRef(0);
  useEffect(() => {
    if (notifications.length > prevCountRef.current) {
      if (prevCountRef.current > 0) {
        const newest = notifications[0];
        if (newest && !newest.read) {
          setActiveNotification({
            id: newest.id,
            title: newest.title,
            message: newest.message,
            image: undefined,
            type: 'alert'
          });
          try { new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3').play().catch(()=>{}); } catch(e){}
        }
      }
    }
    prevCountRef.current = notifications.length;
  }, [notifications]);

  // ===========================================================================
  // 7. ONBOARDING GUARDS
  // ===========================================================================

  if (loadingProfile) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center z-50 fixed inset-0">
        <h1 className="text-7xl md:text-9xl font-black italic tracking-tighter text-white animate-pulse">
          SCOOP<span className="text-white/50">.</span>
        </h1>
      </div>
    );
  }

  if (user && (!profile || !profile.hasSetupProfile)) {
    return (
      <div className="bg-neutral-900 min-h-screen font-sans flex justify-center">
        <div className="w-full max-w-md h-screen bg-black relative shadow-2xl overflow-hidden flex flex-col border-x border-white/10">
          <ProfileSetup 
            profile={(profile || { uid: user.uid, email: user.email }) as any}
            onComplete={async (updates) => {
              try {
                await updateProfile({...updates, hasSetupProfile: true});
              } catch (error: any) {
                console.error("App.tsx caught updateProfile error:", error);
                throw error; 
              }
            }} 
          />
        </div>
      </div>
    );
  }

  if (user && profile && (!profile.stylePreferences || profile.stylePreferences.length === 0)) {
    return (
      <div className="bg-neutral-900 min-h-screen font-sans flex justify-center">
        <div className="w-full max-w-md h-screen bg-black relative shadow-2xl overflow-hidden flex flex-col border-x border-white/10">
          <StyleOnboarding 
            profile={profile} 
            onComplete={() => { updateProfile({ stylePreferences: ['streetwear'] }); }} 
          />
        </div>
      </div>
    );
  }

  // ===========================================================================
  // 8. MAIN RENDER
  // ===========================================================================

  return (
    <>
      {/* 🟢 THE "MEMBERS ONLY" WARNING POPUP */}
      {isAuthPromptOpen && (
        <div className="fixed inset-0 z-[300] bg-black flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-black border border-zinc-800 p-8 rounded-[2rem] lg:rounded-none max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95 duration-300">
                
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_20px_rgba(255,255,255,0.2)] overflow-hidden p-1">
                    <img src="/favicon (2).png" alt="Scoop Logo" className="w-full h-full object-contain rounded-full" />
                </div>

                <h2 className="text-3xl font-black italic uppercase text-white mb-2 tracking-tighter">Join Scoophype</h2>
                
                <p className="text-zinc-400 text-[10px] uppercase tracking-widest font-mono mb-8 leading-relaxed px-2">
                    {authMessage}
                </p>
                
                <div className="flex flex-col gap-3">
                    <button 
                        onClick={() => {
                            setIsAuthPromptOpen(false); 
                            setIsAuthModalOpen(true);   
                        }} 
                        className="w-full py-4 bg-white text-black font-black uppercase text-xs tracking-[0.2em] rounded-xl lg:rounded-none hover:bg-zinc-200 transition-colors shadow-lg"
                    >
                        Sign In / Join
                    </button>
                    <button 
                        onClick={() => setIsAuthPromptOpen(false)} 
                        className="w-full py-4 text-zinc-600 hover:text-white font-black uppercase tracking-[0.2em] text-[10px] transition-colors"
                    >
                        Keep Browsing
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* 🟢 THE MAIN FULL-SCREEN AUTH MODAL */}
      {isAuthModalOpen && (
        <AuthModal 
          onComplete={(newProfile: any) => { 
             updateProfile(newProfile);
             setIsAuthModalOpen(false); 
          }} 
          onClose={() => setIsAuthModalOpen(false)}
        />
      )}

      <InAppNotification 
        notification={activeNotification} 
        onDismiss={() => setActiveNotification(null)} 
        onOpen={() => { setActiveNotification(null); setIsMessagesOpen(true); }} 
      />
      <MessageNotifier />
      <Toast message={toast.message} isVisible={toast.show} onClose={() => setToast({ ...toast, show: false })} type={toast.type} />

      <NotificationsModal 
          isOpen={isNotificationsOpen} 
          onClose={() => setIsNotificationsOpen(false)} 
          notifications={notifications} 
          userId={user?.uid} 
      />

      <MessagesPanel 
          isOpen={isMessagesOpen} 
          onClose={() => {
              setIsMessagesOpen(false);
              setActiveMessageUserId(null); 
              setActiveMessageText(''); 
              setPendingGiftAction(null); 
          }} 
          currentUser={profile} 
          onViewProfile={(userId) => navigate(`/profile/${userId}`)}
          onViewBrand={handleViewBrand} 
          onSelectProduct={setSelectedProduct}
          initialChatUserId={activeMessageUserId}
          initialMessageText={activeMessageText}
          initialGiftAction={pendingGiftAction} 
      />
      
      <ShareModal 
        shareData={shareData}
        isOpen={!!shareData}
        onClose={() => setShareData(null)}
        userProfile={profile}
        requireAuth={requireAuth}
      />

      <CreationMenu 
        isOpen={isCreationMenuOpen} 
        onClose={() => setIsCreationMenuOpen(false)} 
        onAction={handleCreationAction} 
      />
      
      <CreatePostModal 
        isOpen={isPostModalOpen} 
        onClose={() => setIsPostModalOpen(false)} 
      />

      <CartOverlay 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cart={cart} 
        updateQuantity={updateCartQuantity} 
        remove={removeFromCart} 
        checkout={() => requireAuth(() => { 
            setIsCartOpen(false); 
            setIsPaymentOpen(true); 
        }, "Log in to proceed to checkout.")} 
      />
      
      <PaymentModal 
          isOpen={isPaymentOpen} 
          onClose={() => {
              setIsPaymentOpen(false);
              setDirectCheckoutItem(null); 
          }} 
          cart={directCheckoutItem ? directCheckoutItem : cart} 
          onCheckoutComplete={(details) => {
              handleCheckoutComplete(details);
              setDirectCheckoutItem(null); 
          }} 
          profile={profile} 
      />

      <OrderTrackingModal isOpen={isTrackingOpen} onClose={() => setIsTrackingOpen(false)} order={trackingOrder} />
      <ScoopAIModal isOpen={isScoopAIOpen} onClose={() => setIsScoopAIOpen(false)} contextProduct={aiContextProduct} onSelectProduct={setSelectedProduct} cart={cart} wishlist={wishlist} />
      {isVirtualTryOnOpen && <VirtualTryOnModal isOpen={isVirtualTryOnOpen} onClose={() => setIsVirtualTryOnOpen(false)} />}
      <SizeGuideModal 
          isOpen={isSizeGuideOpen} 
          onClose={() => setIsSizeGuideOpen(false)} 
          brandName={selectedProduct?.brand || (selectedProduct as any)?.brandName || (selectedProduct as any)?.businessName || ''} 
      />
      
      <CopOrDropModal isOpen={isCopOrDropOpen} onClose={() => setIsCopOrDropOpen(false)} products={products} onCopVote={handleCopVote} onDropVote={handleDropVote} onComplete={handleGameComplete} currentItem={currentGameItem} score={gameScore} />
      
      {isThreadGameOpen && (
        <ThreadForThread 
          onClose={() => setIsThreadGameOpen(false)} 
          requireAuth={requireAuth} 
        />
      )}

      {/* 🟢🟢🟢 RENDER ALL GAME MODALS 🟢🟢🟢 */}
      {isMysteryDropOpen && (
        <MysteryDropModal 
          isOpen={isMysteryDropOpen} 
          onClose={() => setIsMysteryDropOpen(false)} 
          onOpenBuyCredits={() => {
            setIsMysteryDropOpen(false);
            setIsBuyCreditsOpen(true);
          }}
        />
      )}

      {isHyceOpen && (
        <Hyce 
          currentUser={profile} 
          onClose={() => setIsHyceOpen(false)} 
        />
      )}

      {isHypeStackerOpen && (
        <HypeStacker 
          isOpen={isHypeStackerOpen}
          onClose={() => setIsHypeStackerOpen(false)}
          currentUser={profile}
          updateProfile={updateProfile}
          onBuyCredits={() => {
            setIsHypeStackerOpen(false);
            setIsBuyCreditsOpen(true);
          }}
        />
      )}

      {isBallDropOpen && (
        <HypeHighRoller 
          isOpen={isBallDropOpen}
          onClose={() => setIsBallDropOpen(false)}
          currentUser={profile}
          updateProfile={updateProfile}
          onBuyCredits={() => {
            setIsBallDropOpen(false);
            setIsBuyCreditsOpen(true);
          }}
        />
      )}

      {isGlassBridgeOpen && (
        <GlassBridge 
          isOpen={isGlassBridgeOpen}
          onClose={() => setIsGlassBridgeOpen(false)}
          currentUser={profile}
          updateProfile={updateProfile}
          onBuyCredits={() => {
            setIsGlassBridgeOpen(false);
            setIsBuyCreditsOpen(true);
          }}
        />
      )}

      {isZwepzOpen && (
        <Zwepz 
          isOpen={isZwepzOpen}
          onClose={() => setIsZwepzOpen(false)}
          currentUser={profile}
          updateProfile={updateProfile}
          onBuyCredits={() => {
            setIsZwepzOpen(false);
            setIsBuyCreditsOpen(true);
          }}
        />
      )}

      <HypeCrashModal 
          isOpen={isHypeCrashOpen} 
          onClose={() => setIsHypeCrashOpen(false)} 
          profile={profile} 
          updateProfile={updateProfile} 
          onBuyCredits={() => {
              setIsHypeCrashOpen(false);
              setIsBuyCreditsOpen(true);
          }}
      />
      <HypeMinesModal 
          isOpen={isHypeMinesOpen} 
          onClose={() => setIsHypeMinesOpen(false)} 
          profile={profile} 
          updateProfile={updateProfile} 
          onBuyCredits={() => {
              setIsHypeMinesOpen(false);
              setIsBuyCreditsOpen(true);
          }}
      />
      <HypeShellsModal 
          isOpen={isHypeShellsOpen} 
          onClose={() => setIsHypeShellsOpen(false)} 
          profile={profile} 
          updateProfile={updateProfile} 
          onBuyCredits={() => {
              setIsHypeShellsOpen(false);
              setIsBuyCreditsOpen(true);
          }}
      />

      {profile && (
        <>
          <BuyCreditsModal isOpen={isBuyCreditsOpen} onClose={() => setIsBuyCreditsOpen(false)} profile={profile} onPurchase={handlePurchaseCredits} />
          <InviteFriendsModal isOpen={isInviteFriendsOpen} onClose={() => setIsInviteFriendsOpen(false)} profile={profile} />
          <EditProfileModal isOpen={isEditProfileOpen} onClose={() => setIsEditProfileOpen(false)} profile={profile} onComplete={updateProfile} />
          <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} profile={profile} onUpdate={updateProfile} onSignOut={() => signOut(auth)} />
        </>
      )}

      {activeBrand === 'love-scoop' ? (
        <LoveScoopStore 
            userProfile={profile} 
            onUpdateProfile={updateProfile} 
            onClose={() => setActiveBrand(null)} 
            onOpenBuyCredits={() => setIsBuyCreditsOpen(true)} 
        />
      ) : activeBrand ? (
        <BrandProfileView 
          brand={brands.find(b => b.id === activeBrand || (b as any).businessName === activeBrand || b.name === activeBrand) as any}
          products={products.filter(p => {
              const foundBrand = brands.find(b => b.id === activeBrand || (b as any).businessName === activeBrand || b.name === activeBrand) as any;
              return p.businessId === activeBrand || (foundBrand && (p.brand === foundBrand.businessName || p.brand === foundBrand.name));
          })}
          onClose={() => setActiveBrand(null)}
          onSelectProduct={setSelectedProduct}
          userProfile={profile || undefined}
          onUpdateProfile={updateProfile}
          wishlist={wishlist}
          onToggleWishlist={handleToggleWishlist}
          onShareProfile={() => requireAuth(() => {
              const bObj = brands.find(b => b.id === activeBrand || (b as any).businessName === activeBrand || b.name === activeBrand);
              setPendingGiftAction({ type: 'share_brand', data: bObj });
              setIsMessagesOpen(true);
              setActiveBrand(null);
          }, "Log in to share this brand.")}
          requireAuth={requireAuth}
        />
      ) : null}

      {/* --- ROUTING & VIEWS --- */}
      <Routes>
        
        {/* 🟢 HYPE COMMERCE / POP-UP ROUTE */}
        <Route path="/pop/:brandId" element={<HypeCommerce />} />
        
        {/* 🟢 THRIFT ROUTE */}
        <Route 
          path="/thrifts" 
          element={
            <ThriftView
              wishlist={wishlist || []}
              onToggleWishlist={handleToggleWishlist}
              onSelectProduct={setSelectedProduct}
              onOpenSellModal={() => requireAuth(() => setIsPostModalOpen(true), "Log in to sell your pieces.")}
              onShareProduct={(item) => setShareData({ type: 'post', payload: item })}
              onViewProfile={(userId) => navigate(`/thrift-profile/${userId}`)}
            />
          } 
        />

        {/* 🟢 THRIFT PROFILE ROUTE */}
        <Route 
          path="/thrift-profile/:thriftId" 
          element={
            <ThriftProfilePage 
              onClose={() => navigate(-1)} 
            />
          } 
        />

        {/* 🟢 DESIGNER PROFILE ROUTE */}
        <Route 
          path="/designer-profile/:designerId" 
          element={
            <DesignerProfilePage 
              onClose={() => navigate(-1)} 
            />
          } 
        />

        {/* 🟢 DESIGNER GATE ROUTE */}
        <Route 
          path="/designer" 
          element={
            <DesignerGateView
              wishlist={wishlist || []}
              onToggleWishlist={handleToggleWishlist}
              onSelectProduct={(p: any) => setSelectedProduct(p)}
              onOpenSellModal={() => requireAuth(() => setIsPostModalOpen(true), "Log in to list your products.")}
              onViewProfile={(userId) => navigate(`/profile/${userId}`)}
              onSendMessage={(sellerId, sellerName) => {
                requireAuth(() => {
                  setActiveMessageUserId(sellerId);
                  setActiveMessageText(`Hi, I'm interested in your products on Designer Gate.`);
                  setIsMessagesOpen(true);
                }, "Log in to message this supplier.");
              }}
            />
          } 
        />

        {/* 🟢 JEWELLERY ROUTE */}
        <Route 
          path="/jewellery" 
          element={
            <JewelleryView
              wishlist={wishlist || []}
              onToggleWishlist={handleToggleWishlist}
              onSelectProduct={(p: any) => setSelectedProduct(p)}
              onViewProfile={(userId) => navigate(`/profile/${userId}`)}
              onSendMessage={(sellerId, sellerName) => {
                requireAuth(() => {
                  setActiveMessageUserId(sellerId);
                  setActiveMessageText(`Hi, I'm interested in your jewellery pieces.`);
                  setIsMessagesOpen(true);
                }, "Log in to message this jeweller.");
              }}
            />
          } 
        />

        {/* 🟢 HYPE ARCADE ROUTE */}
        <Route 
          path="/games" 
          element={
            <HypeArcadeView
              onOpenMysteryDrop={() => setIsMysteryDropOpen(true)}
              onOpenHyce={() => setIsHyceOpen(true)}
              onOpenCopOrDrop={handleOpenCopOrDrop}
              onOpenHypeStacker={() => setIsHypeStackerOpen(true)}
              onOpenBallDrop={() => setIsBallDropOpen(true)}
              onOpenGlassBridge={() => setIsGlassBridgeOpen(true)}
              onOpenZwepz={() => setIsZwepzOpen(true)}
            />
          } 
        />

        <Route element={<ClientLayout />}>
          {/* 🟢 HOME VIEW - NOW USING FILTERED brandProfiles */}
          <Route path="/" element={
            <HomeView 
              profile={profile} 
              products={products} 
              brands={brandProfiles}
              wishlist={wishlist}
              onSelectProduct={setSelectedProduct} 
              onViewBrand={handleViewBrand}
              onToggleWishlist={handleToggleWishlist} 
              onOpenCopOrDrop={handleOpenCopOrDrop}
              onOpenVirtualTryOn={() => setIsVirtualTryOnOpen(true)}
              updateProfile={updateProfile}
            />
          } />
          
          <Route path="/search" element={
            <SearchView 
              products={products} brands={brands} searchQuery={searchQuery}
              onSearchChange={setSearchQuery} onSelectProduct={setSelectedProduct} onViewBrand={handleViewBrand}
              wishlist={wishlist} onToggleWishlist={handleToggleWishlist}
            />
          } />
          
          <Route path="/community" element={
            <CommunityView 
              onViewProfile={(uid) => navigate(`/profile/${uid}`)} 
              onSelectProduct={setSelectedProduct} 
              onOpenCreatePost={() => requireAuth(() => setIsPostModalOpen(true), "Log in to share your fit.")}  
              onOpenCreateHook={() => requireAuth(() => setIsCreateHookOpen(true), "Log in to create a hook.")} 
              onMessageUser={(uid, initialMessage) => requireAuth(() => { 
                  setActiveMessageUserId(uid); 
                  if (initialMessage) setActiveMessageText(initialMessage);
                  setIsMessagesOpen(true); 
              }, "Log in to message this user.")} 
              requireAuth={requireAuth}
            />
          } />
          
          <Route path="/profile" element={
            profile ? (
                <ProfileView 
                profile={profile} products={products} orders={orders} wishlist={wishlist} followingBrands={profile.followingBrands || []}
                onEditProfile={() => setIsEditProfileOpen(true)} onOpenSettings={() => setIsSettingsOpen(true)}
                onBuyCredits={() => setIsBuyCreditsOpen(true)} onInviteFriends={() => setIsInviteFriendsOpen(true)}
                onSelectProduct={setSelectedProduct} onViewBrand={handleViewBrand} onTrackOrder={handleTrackOrder}
                onUpdateProfile={updateProfile} onSignOut={() => signOut(auth)}
                onViewProfile={(uid) => navigate(`/profile/${uid}`)}
                />
            ) : (
                <div className="h-screen pt-24 bg-black flex flex-col justify-center items-center">
                    <div className="w-20 h-20 bg-zinc-950 border border-zinc-800 rounded-full flex items-center justify-center mb-6">
                        <User className="text-white" size={32} />
                    </div>
                    <div className="text-white font-black italic text-4xl tracking-tighter uppercase mb-3 text-center px-8">
                        Join Scoophype
                    </div>
                    <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest text-center px-8 mb-8 max-w-xs leading-relaxed">
                        Create an account to view your profile, track orders, and manage your collection.
                    </p>
                    <button 
                        onClick={() => {
                            setAuthMessage("Log in to view your profile and manage orders.");
                            setIsAuthModalOpen(true);
                        }}
                        className="bg-white text-black px-10 py-4 rounded-full lg:rounded-none text-xs font-black uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)]"
                    >
                        Sign In
                    </button>
                </div>
            )
          } />
          
          <Route path="/profile/:id" element={
              <DynamicPublicProfile 
                  currentUser={profile}
                  onFollow={handleFollowUser}
                  onUnfollow={handleUnfollowUser}
                  onMessage={(uid: string) => requireAuth(() => { setActiveMessageUserId(uid); setIsMessagesOpen(true); }, "Log in to send a message.")}
                  onSelectProduct={setSelectedProduct}
              />
          } />

          <Route path="/collections" element={<CollectionsView onSelectProduct={setSelectedProduct} />} />
          <Route path="/invite/:id" element={<ReferralLanding />} />
          <Route path="/editorial" element={<div className="min-h-screen flex items-center justify-center text-white">Editorial Coming Soon</div>} />
        </Route>
      </Routes>

      {/* 🟢🟢🟢 PRODUCT DETAIL MODAL - RENDERED VIA PORTAL (ON TOP OF EVERYTHING) 🟢🟢🟢 */}
      <Portal>
        <ProductDetailModal 
          product={selectedProduct} 
          isOpen={!!selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
          onAddToCart={(product, size, color, quantity) => addToCart({ ...product, selectedSize: size, selectedColor: color, quantity })} 
          onBuyNow={handleBuyNow} 
          onViewBrand={handleViewBrand} 
          onOpenAI={handleOpenAI}
          onOpenSizeGuide={() => setIsSizeGuideOpen(true)}
          isWishlisted={selectedProduct ? (wishlist || []).includes(selectedProduct.id) : false}
          onToggleWishlist={() => selectedProduct && handleToggleWishlist(selectedProduct.id)}
          onShareToDM={(product) => requireAuth(() => {
             setPendingGiftAction({ type: 'share_product', data: product });
             setIsMessagesOpen(true);
             setSelectedProduct(null);
          }, "Log in to share this product.")}
          onRequestGift={(product) => requireAuth(() => {
             setPendingGiftAction({ type: 'request', data: product });
             setIsMessagesOpen(true);
             setSelectedProduct(null);
          }, "Log in to request this product as a gift.")}
          onSendGift={(product) => requireAuth(() => {
             setPendingGiftAction({ type: 'send', data: product });
             setIsMessagesOpen(true);
             setSelectedProduct(null);
          }, "Log in to send this product as a gift.")}
        />
      </Portal>

    </>
  );
}

// 🟢 HELMET PROVIDER ADDED TO WRAP THE ENTIRE APP
export default function CompleteScoopApp() {
  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <AppContent />
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}